import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RotateCw } from 'lucide-react';
import type { CliActionResult } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { Button, Card, ErrorNotice, Field, Spinner } from '../../components/ui';

interface SpeedProfileOption {
  id: string;
  name: string;
  direction: 'up' | 'down';
}

/** What the dialog hands back on Apply — the page sends it with the resync. */
export interface ResyncApplyOptions {
  /** The upstream speed profile the operator chose for a bare interface, if any. */
  upstreamProfileId?: string;
  /** Reboot the ONT first and write while it is off the PON (2026-10-16). */
  reboot?: boolean;
}

/**
 * Resync, reviewed before it runs.
 *
 * The button used to BE the write: one click re-stated a live subscriber.
 * Resync also grew teeth — it rebuilds a missing management stack and now
 * REMOVES stray management-flow lines — so what it is about to do belongs on
 * screen first. The dialog runs the API's own dry run: the same plan the
 * apply will execute, warnings included, with the exact commands behind a
 * fold for whoever is diagnosing at that depth.
 *
 * It also absorbed Reprovision (operator, 2026-09-09: one button). A ZTE C300
 * interface an authorise left with no T-CONT/GEM behind a vport makes the plan
 * refuse rather than guess a rate; the dry run says so with
 * `needsUpstreamProfile`, and the dialog then offers the upstream speed
 * profile picker in place — the same source and vocabulary the old
 * Reprovision dialog used: real profile names, never ids — re-runs the dry run
 * with the choice, and the reprovision lines appear in the same command list
 * as the resync's. When the plan filled a bare interface from the org's
 * highest profile on its own (`upstreamProfileDefault`), the picker shows that
 * default and lets the operator change it.
 */
export function ResyncConfirmDialog({
  oltId,
  onuId,
  serial,
  applying,
  onApply,
  onClose,
}: {
  oltId: string;
  onuId: string;
  serial: string;
  applying: boolean;
  onApply: (opts: ResyncApplyOptions) => void;
  onClose: () => void;
}) {
  // '' = no choice made: the plan runs as a plain resync.
  const [profileId, setProfileId] = useState('');
  const [reboot, setReboot] = useState(false);

  const planQuery = useQuery({
    queryKey: ['onu', onuId, 'resync-plan', profileId, reboot],
    queryFn: () =>
      api.post<CliActionResult>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/resync`,
        { dryRun: true, ...(profileId ? { upstreamProfileId: profileId } : {}), ...(reboot ? { reboot: true } : {}) },
      ),
    staleTime: 0,
    gcTime: 0,
  });
  const plan = planQuery.data;

  const needsProfile = plan?.needsUpstreamProfile === true;
  const defaulted = plan?.upstreamProfileDefault ?? null;
  // The picker is on screen when the plan asks for a rate, when it defaulted
  // one, or once the operator has chosen (so the choice can be changed back).
  const showPicker = needsProfile || defaulted !== null || profileId !== '';

  const profiles = useQuery({
    queryKey: ['speed-profiles'],
    queryFn: () => api.get<{ items: SpeedProfileOption[] }>('/speed-profiles'),
    enabled: showPicker,
  });
  const upstream = (profiles.data?.items ?? []).filter((p) => p.direction === 'up');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg">
        <Card
          title={
            <span className="flex items-center gap-2">
              <RotateCw className="size-4 text-muted" />
              Resync — {serial}
            </span>
          }
        >
          <div className="space-y-4 p-5">
            {planQuery.isLoading ? (
              <div className="flex justify-center p-6">
                <Spinner />
              </div>
            ) : planQuery.isError ? (
              <ErrorNotice error={planQuery.error as Error} />
            ) : plan ? (
              <>
                {needsProfile ? (
                  <p className="text-sm text-muted">
                    This ONU's chassis-side interface has no T-CONT or GEM behind its vports — an
                    authorise left it bare — so the resync will not guess a rate. Choose the
                    subscriber's upstream speed profile and the resync creates the T-CONTs from it
                    first, then re-states the VLANs and ports.
                  </p>
                ) : (
                  <p className="text-sm text-muted">
                    Re-states what SimpleACS holds for this subscriber onto the OLT. A subscriber whose
                    configuration already matches notices nothing.
                  </p>
                )}

                {showPicker && (
                  <Field
                    label="Upstream speed profile"
                    hint={
                      needsProfile
                        ? "The rate the new T-CONTs are created with — pick the subscriber's real upstream speed, because this sets it. It is created on the chassis first if it is not already there."
                        : defaulted && !profileId
                          ? `The interface has no T-CONT of its own, so the resync fills it from "${defaulted}" — the highest upstream profile, the standing default. Change it here if this subscriber's rate is different.`
                          : 'The rate the new T-CONTs are created with. It is created on the chassis first if it is not already there.'
                    }
                  >
                    {profiles.isLoading ? (
                      <p className="text-sm text-muted">Loading…</p>
                    ) : profiles.isError ? (
                      <ErrorNotice error={profiles.error as Error} />
                    ) : upstream.length === 0 ? (
                      <p className="text-sm text-warn">
                        This organization has no upstream speed profiles yet — create one under
                        Settings → Speed profiles first.
                      </p>
                    ) : (
                      <select
                        className="input"
                        value={profileId}
                        onChange={(e) => setProfileId(e.target.value)}
                      >
                        <option value="">
                          {defaulted ? `${defaulted} — the highest, the default` : 'Choose a profile…'}
                        </option>
                        {upstream
                          .filter((p) => !(defaulted && p.name === defaulted))
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                      </select>
                    )}
                  </Field>
                )}

                {/* The reboot window (2026-10-16): a word an ONT declines while up — a
                    Huawei ONT's per-socket DHCP word on a ZTE chassis — is stored only
                    when the chassis writes it while the ONT is off the PON. */}
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={reboot} onChange={(e) => setReboot(e.target.checked)} />
                  <span>
                    Reboot the ONU first and write while it is off the PON
                    <span className="block text-xs text-faint">
                      For an ONT that declines a word while it is up. The subscriber is down for a minute or two.
                    </span>
                  </span>
                </label>

                {(plan.warnings ?? []).map((w) => (
                  <div
                    key={w}
                    className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                  >
                    {w}
                  </div>
                ))}
              </>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 border-t border-line px-5 py-4">
            <Button onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<RotateCw className="size-4" />}
              loading={applying}
              // A refusing plan has nothing to apply: the picker re-runs the
              // dry run, and Apply opens once that plan has commands.
              disabled={planQuery.isLoading || planQuery.isError || needsProfile}
              onClick={() => onApply({ ...(profileId ? { upstreamProfileId: profileId } : {}), ...(reboot ? { reboot: true } : {}) })}
            >
              Apply
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
